import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Wrench } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  Input,
  Page,
  PageHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { LoadFailed } from '../components/load-failed.tsx'
import {
  ApiError,
  createApplicationBrand,
  createApplicationModel,
  fetchApplicationBrands,
  fetchApplicationModels,
  type ApplicationBrandSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Aplicações — what a part fits (§13, F031).
 *
 * The register behind the counter's question. Two levels: brands, and the
 * models under one of them.
 *
 * A tree rather than flat labels, and the screen has to make that visible or
 * people will fight it. Choosing a brand first is not a step to be endured; it
 * is what stops "A20", "Galaxy A20" and "SAMSUNG A20" becoming three rows that
 * no search connects — which is exactly what happens today, in the product's
 * own name.
 *
 * The compatibility itself is attached on the PRODUCT screen, not here. This is
 * the vocabulary; using it belongs where the part is.
 */

const NameDialog = ({
  title,
  description,
  label,
  extra,
  onSubmit,
  onClose,
}: {
  title: string
  description: string
  label: string
  extra?: { label: string; hint: string } | undefined
  onSubmit: (name: string, extra: string) => Promise<void>
  onClose: () => void
}): ReactNode => {
  const [name, setName] = useState('')
  const [extraValue, setExtraValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      await onSubmit(name.trim(), extraValue.trim())
    },
    onSuccess: onClose,
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError ? cause.message : 'Não foi possível salvar.'
      )
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={title}
        description={description}
        className="max-w-md"
      >
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <Field label={label} required>
            {(field) => (
              <Input
                {...field}
                value={name}
                autoFocus
                maxLength={80}
                onChange={(event) => {
                  setName(event.target.value)
                }}
              />
            )}
          </Field>

          {extra === undefined ? null : (
            <Field label={extra.label} hint={extra.hint}>
              {(field) => (
                <Input
                  {...field}
                  value={extraValue}
                  maxLength={60}
                  onChange={(event) => {
                    setExtraValue(event.target.value)
                  }}
                />
              )}
            </Field>
          )}

          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={save.isPending || name.trim().length === 0}
            >
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export const AplicacoesPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const canWrite = allows('catalog.write')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [brandDialog, setBrandDialog] = useState(false)
  const [modelDialog, setModelDialog] = useState(false)

  const brands = useQuery({
    queryKey: ['application-brands'],
    queryFn: async ({ signal }) =>
      await fetchApplicationBrands('active', signal),
  })

  const rows: ApplicationBrandSummaryType[] = brands.data?.brands ?? []
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0] ?? null

  const models = useQuery({
    queryKey: ['application-models', selected?.id],
    queryFn: async ({ signal }) =>
      selected === null
        ? { models: [] }
        : await fetchApplicationModels({ brandId: selected.id }, signal),
    enabled: selected !== null,
  })

  return (
    <Page>
      <PageHeader
        title="Aplicações"
        description="Em que uma peça serve. Marca e modelo, para que a busca do balcão responda “esta tela serve no A20?”."
        actions={
          canWrite ? (
            <Button
              onClick={() => {
                setBrandDialog(true)
              }}
            >
              <Plus aria-hidden="true" /> Nova marca
            </Button>
          ) : null
        }
      />

      {brands.isPending ? (
        <Spinner />
      ) : brands.isError ? (
        <LoadFailed
          what="as marcas"
          onRetry={() => {
            void brands.refetch()
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Wrench aria-hidden="true" />}
          title="Nenhuma marca cadastrada"
          description="Comece pela marca do aparelho ou do veículo — Samsung, Brastemp, Consul — e depois cadastre os modelos dela."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Marca</TableHeaderCell>
                  <TableHeaderCell>Modelos</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((brand) => (
                  <TableRow key={brand.id}>
                    <TableCell>
                      <button
                        type="button"
                        className="text-left underline-offset-2 hover:underline"
                        aria-current={
                          brand.id === selected?.id ? 'true' : undefined
                        }
                        onClick={() => {
                          setSelectedId(brand.id)
                        }}
                      >
                        {brand.name}
                      </button>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {brand.modelCount}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {selected === null ? null : (
            <div className="flex flex-col gap-3">
              <PageHeader
                title={`Modelos de ${selected.name}`}
                description="O nome que está escrito no aparelho. Acentos e maiúsculas não criam um modelo novo."
                actions={
                  canWrite ? (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setModelDialog(true)
                      }}
                    >
                      <Plus aria-hidden="true" /> Novo modelo
                    </Button>
                  ) : null
                }
              />

              {models.isPending ? (
                <Spinner />
              ) : models.isError ? (
                <LoadFailed
                  what="os modelos desta marca"
                  onRetry={() => {
                    void models.refetch()
                  }}
                />
              ) : models.data.models.length === 0 ? (
                <EmptyState
                  icon={<Wrench aria-hidden="true" />}
                  title="Nenhum modelo nesta marca"
                  description="Cadastre os modelos para poder ligá-los às peças no cadastro do produto."
                />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Modelo</TableHeaderCell>
                        <TableHeaderCell>Referência</TableHeaderCell>
                        <TableHeaderCell>Situação</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {models.data.models.map((model) => (
                        <TableRow key={model.id}>
                          <TableCell>{model.name}</TableCell>
                          <TableCell className="font-(--font-mono)">
                            {model.reference ?? '—'}
                          </TableCell>
                          <TableCell>
                            <Badge
                              tone={
                                model.status === 'active'
                                  ? 'success'
                                  : 'neutral'
                              }
                            >
                              {model.status === 'active'
                                ? 'Ativo'
                                : 'Arquivado'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          )}
        </div>
      )}

      {brandDialog ? (
        <NameDialog
          title="Nova marca"
          description="A marca do aparelho ou do veículo, não a do produto que você vende."
          label="Nome da marca"
          onSubmit={async (name) => {
            await createApplicationBrand({ name })
            await queryClient.invalidateQueries({
              queryKey: ['application-brands'],
            })
          }}
          onClose={() => {
            setBrandDialog(false)
          }}
        />
      ) : null}

      {modelDialog && selected !== null ? (
        <NameDialog
          title={`Novo modelo de ${selected.name}`}
          description="O nome que o cliente usa. A referência é o código do fabricante, quando ele traz."
          label="Nome do modelo"
          extra={{
            label: 'Referência',
            hint: 'Opcional. Ex.: SM-A205G. É pesquisável junto com o nome.',
          }}
          onSubmit={async (name, reference) => {
            await createApplicationModel({
              brandId: selected.id,
              name,
              reference: reference === '' ? null : reference,
            })
            await queryClient.invalidateQueries({
              queryKey: ['application-models', selected.id],
            })
            // The brand list carries a model count, so it is stale now too.
            await queryClient.invalidateQueries({
              queryKey: ['application-brands'],
            })
          }}
          onClose={() => {
            setModelDialog(false)
          }}
        />
      ) : null}
    </Page>
  )
}
